// Guarantees that anything the app reads back from /api/ is JSON.
//
// Every call site in this app does `const json = await res.json()`. When the
// backend is down, restarting, or still cold-starting, the request never
// reaches Express — Render/Cloudflare answer it themselves with an HTML error
// page. `res.json()` then throws its parser error, and because most call sites
// display `err.message` directly, users were shown the raw text
// `Unexpected token '<', "<!DOCTYPE "... is not valid JSON`.
//
// Every /api/ route on this server responds with JSON (there is no sendFile,
// res.send or non-JSON content type anywhere in it), so a non-JSON body under
// /api/ always means the request died somewhere in front of the app. That
// makes it safe to swap such a response for a real JSON one carrying a
// readable message, which every existing `if (!res.ok) setError(json.error)`
// branch then handles on its own — no changes needed at the ~60 call sites.

const jsonResponse = (status, error) =>
    new Response(JSON.stringify({ error }), {
        status,
        headers: { 'Content-Type': 'application/json' },
    });

const messageForStatus = (status) => {
    if (status === 502 || status === 503 || status === 504) {
        return 'The server is temporarily unavailable. It may be restarting — please try again in a moment.';
    }
    if (status === 429) {
        return 'Too many requests. Please wait a moment and try again.';
    }
    if (status === 413) {
        return 'That file is too large to upload.';
    }
    if (status >= 500) {
        return 'Something went wrong on our side. Please try again in a moment.';
    }
    if (status === 404) {
        return 'That request could not be reached. Please refresh the page and try again.';
    }
    return `The server returned an unexpected response (${status}). Please try again.`;
};

const urlOf = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input?.url || '';
};

export function installApiGuard() {
    if (typeof window === 'undefined' || window.__apiGuardInstalled) return;
    window.__apiGuardInstalled = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init) => {
        if (!urlOf(input).includes('/api/')) return originalFetch(input, init);

        let response;
        try {
            response = await originalFetch(input, init);
        } catch (err) {
            // A deliberate abort is control flow, not a failure — callers that
            // use AbortController expect to catch it themselves.
            if (err?.name === 'AbortError') throw err;
            return jsonResponse(
                503,
                'Could not reach the server. Check your internet connection and try again.'
            );
        }

        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('json')) return response;

        // A 200 carrying HTML means a proxy or static-host fallback answered
        // instead of the API — just as broken as an explicit 5xx, so it is
        // reported as a gateway failure rather than a success.
        if (response.ok) {
            if (!contentType.includes('html')) return response;
            return jsonResponse(
                502,
                'The server is temporarily unavailable. It may be restarting — please try again in a moment.'
            );
        }

        return jsonResponse(response.status, messageForStatus(response.status));
    };
}
